import React from 'react';
import Card from '../../../Presentation/components/ui/Card';
import AppInput from '../../../Presentation/components/ui/AppInput';
import Button from '../../../Presentation/components/ui/Button';
import {Filter, X, Search} from 'lucide-react';
import {PaymentFilterParams} from '../models/Payment';
import {usePaymentsUIStore} from '../store/paymentsUIStore';

interface PaymentFiltersProps {
  onFilterChange: (filters: PaymentFilterParams) => void;
  onClearFilters: () => void;
}

const PaymentFilters: React.FC<PaymentFiltersProps> = ({
  onFilterChange,
  onClearFilters
}) => {
  const {
    filters, setFilters, showFilters, setShowFilters, clearFilters
  } = usePaymentsUIStore();

  const handleInputChange = (field: keyof PaymentFilterParams, value: any) => {
    setFilters({[field]: value});
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    clearFilters();
    onClearFilters();
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">فلاتر المدفوعات</h3>
        </div>
        <Button
          variant="outline"
          size="small"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'إخفاء الفلاتر' : 'إظهار الفلاتر'}
        </Button>
      </div>

      {showFilters && (
        <div className="space-y-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              البحث
            </label>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <AppInput
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleInputChange('search', e.target.value)}
                placeholder="البحث في رقم المدفوعة أو اسم العميل"
                className="pr-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Payment Method Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                طريقة الدفع
              </label>
              <select
                value={filters.paymentMethod || ''}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value || undefined)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
              >
                <option value="">جميع الطرق</option>
                <option value="نقداً">نقداً</option>
                <option value="تحويل">تحويل بنكي</option>
              </select>
            </div>

            {/* Customer Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اسم العميل
              </label>
              <AppInput
                type="text"
                value={filters.customerName || ''}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                placeholder="اسم العميل"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                من تاريخ
              </label>
              <AppInput
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => handleInputChange('dateFrom', e.target.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                إلى تاريخ
              </label>
              <AppInput
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => handleInputChange('dateTo', e.target.value)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleApplyFilters}
              className="bg-brand-600 hover:bg-brand-700 text-white"
            >
              تطبيق الفلاتر
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              مسح الفلاتر
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PaymentFilters;
